import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ClubLogoMark } from "../../components/ClubLogoMark";
import { useBrand } from "../../context/BrandContext";
import {
  closeNewsletterCycle,
  fetchNewsletterCycle,
  fetchNewsletterIssue,
  fetchNewsletterRecipients,
  fetchNewsletters,
  previewNewsletter,
  resendNewsletterIssue,
  saveNewsletterCycle,
} from "../../api/newsletterApi";
import { NewsletterCycleSetup } from "../../components/newsletter/NewsletterCycleSetup";
import { NewsletterIssueBody } from "../../components/newsletter/NewsletterIssueBody";
import { NewsletterPostComposer } from "../../components/newsletter/NewsletterPostComposer";
import { NewsletterMailSettings } from "../../components/newsletter/NewsletterMailSettings";
import { formatIssueRange, usePingfloScoreboardFont } from "../../components/newsletter/newsletterFormat";
import { readUserFromStorage } from "../../utils/planningAccess";
import { userCanManageNewsletter, userCanPostNewsletter } from "../../utils/newsletterAccess";
import "./PingfloNewsletterPage.css";

export default function PingfloNewsletterPage() {
  const { issueId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { brand } = useBrand() || {};
  const [issues, setIssues] = useState([]);
  const [canManageApi, setCanManageApi] = useState(null);
  const [canPostApi, setCanPostApi] = useState(null);
  const [issue, setIssue] = useState(null);
  const [preview, setPreview] = useState(null);
  const [cycle, setCycle] = useState(null);
  const [groups, setGroups] = useState([]);
  const [recipients, setRecipients] = useState(null);
  const [panel, setPanel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  usePingfloScoreboardFont();

  const [roleTick, setRoleTick] = useState(0);
  useEffect(() => {
    const sync = () => setRoleTick((n) => n + 1);
    window.addEventListener("smarttt-home-role-changed", sync);
    window.addEventListener("smarttt-user-updated", sync);
    return () => {
      window.removeEventListener("smarttt-home-role-changed", sync);
      window.removeEventListener("smarttt-user-updated", sync);
    };
  }, []);

  const storedUser = readUserFromStorage();
  // Club Admin / Super Admin configure the sender, the cycle, and send.
  // Coaches can add posts to the next issue. roleTick forces re-check after Home role switches.
  const staff = roleTick >= 0 && userCanManageNewsletter(storedUser) && canManageApi !== false;
  const poster = staff || (userCanPostNewsletter(storedUser) && canPostApi !== false);

  useEffect(() => {
    if (!staff && (panel === "cycle" || panel === "mail")) setPanel(null);
    if (!poster && panel === "posts") setPanel(null);
  }, [staff, poster, panel]);

  const loadList = useCallback(async () => {
    const data = await fetchNewsletters();
    setIssues(data?.issues || []);
    setCanManageApi(typeof data?.can_manage === "boolean" ? data.can_manage : null);
    setCanPostApi(typeof data?.can_post === "boolean" ? data.can_post : null);
    return data;
  }, []);

  useEffect(() => {
    let cancelled = false;
    setError("");
    (async () => {
      try {
        const data = await loadList();
        if (cancelled) return;
        if (!issueId && data?.issues?.[0] && !preview) {
          navigate(`/newsletter/${data.issues[0].issue_id}`, { replace: true });
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [issueId, loadList, navigate, preview]);

  useEffect(() => {
    if (!issueId) {
      setIssue(null);
      return undefined;
    }
    let cancelled = false;
    setPreview(null);
    fetchNewsletterIssue(issueId)
      .then((data) => {
        if (!cancelled) setIssue(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [issueId]);

  useEffect(() => {
    if (!staff) return undefined;
    let cancelled = false;
    fetchNewsletterCycle()
      .then((data) => {
        if (cancelled) return;
        setCycle(data?.cycle || null);
        setGroups(data?.groups || []);
        if (!data?.cycle) setPanel("cycle");
      })
      .catch(() => {
        if (!cancelled) setPanel("cycle");
      });
    return () => {
      cancelled = true;
    };
  }, [staff]);

  const loadRecipients = useCallback(() => {
    fetchNewsletterRecipients()
      .then((data) => setRecipients(data || null))
      .catch(() => setRecipients(null));
  }, []);

  useEffect(() => {
    if (staff && panel === "cycle") loadRecipients();
  }, [staff, panel, loadRecipients]);

  const snapshot = preview?.snapshot || issue?.snapshot || null;
  const range = formatIssueRange(
    snapshot?.cycle?.start || issue?.start_date,
    snapshot?.cycle?.end || issue?.end_date,
    i18n.language
  );

  const onSave = async (body) => {
    if (!staff) return;
    setBusy(true);
    setNotice("");
    try {
      const data = await saveNewsletterCycle(body);
      setCycle(data?.cycle || null);
      setNotice(t("newsletterSaved", "Cycle saved"));
    } finally {
      setBusy(false);
    }
  };

  const onPreview = async (body) => {
    if (!staff) return;
    setBusy(true);
    setNotice("");
    setError("");
    try {
      const data = await previewNewsletter(body);
      setPreview(data);
      setNotice(t("newsletterPreviewLabel", "Preview — not sent"));
    } finally {
      setBusy(false);
    }
  };

  const onClose = async () => {
    if (!staff) return;
    setBusy(true);
    setNotice("");
    setError("");
    try {
      const data = await closeNewsletterCycle();
      setPreview(null);
      setIssue(data);
      await loadList();
      const next = await fetchNewsletterCycle();
      setCycle(next?.cycle || null);
      setNotice(
        t("newsletterSentCount", {
          count: data?.emails_sent || 0,
          defaultValue: "Issue published and emailed to {{count}} members",
        })
      );
      if (data?.issue_id) navigate(`/newsletter/${data.issue_id}`);
    } finally {
      setBusy(false);
    }
  };

  const onResend = async () => {
    if (!staff || !issue?.issue_id) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const data = await resendNewsletterIssue(issue.issue_id);
      setIssue((prev) => (prev ? { ...prev, emails_sent: data?.sent, emails_failed: data?.failed } : prev));
      setNotice(
        data?.failed
          ? t("newsletterResendPartial", {
              count: data.failed,
              defaultValue: "{{count}} emails still failing. Check the mail settings.",
            })
          : t("newsletterResendDone", "All emails delivered")
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const onShare = async () => {
    const url = issue?.public_url;
    if (!url) return;
    try {
      if (navigator.share) {
        await navigator.share({ title: `Pingflo · ${clubName}`, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setNotice(t("newsletterLinkCopied", "Link copied. Anyone with it can read this issue."));
    } catch (err) {
      if (err?.name !== "AbortError") window.prompt(t("newsletterCopyLink", "Copy this link"), url);
    }
  };

  const togglePanel = (name) => setPanel((open) => (open === name ? null : name));

  const clubName =
    storedUser?.club_details?.club_name ||
    storedUser?.club_name ||
    brand?.brand_name ||
    t("newsletterClub", "Club");

  return (
    <main className="pingflo-issue">
      <header className="pingflo-mast">
        <ClubLogoMark clubName={clubName} useBrandFallback size="md" />
        <div>
          <p className="pingflo-mark">Pingflo</p>
          <h1>{clubName}</h1>
          <p>{range}</p>
          {snapshot?.cycle?.group_names?.length ? (
            <p className="pingflo-groups">{snapshot.cycle.group_names.join(" · ")}</p>
          ) : null}
        </div>
      </header>

      {preview ? <p className="pingflo-banner">{t("newsletterPreviewLabel", "Preview — not sent")}</p> : null}
      {notice && !preview ? <p className="pingflo-banner">{notice}</p> : null}
      {error ? <p className="pingflo-error">{error}</p> : null}

      {poster ? (
        <div className="pingflo-toolbar" role="toolbar">
          <button
            type="button"
            className="pingflo-setup-toggle"
            aria-pressed={panel === "posts"}
            onClick={() => togglePanel("posts")}
          >
            {t("newsletterAddPostShort", "Add a post")}
          </button>
          {staff ? (
            <>
              <button
                type="button"
                className="pingflo-setup-toggle"
                aria-pressed={panel === "cycle"}
                onClick={() => togglePanel("cycle")}
              >
                {t("newsletterSetup", "Cycle")}
              </button>
              <button
                type="button"
                className="pingflo-setup-toggle"
                aria-pressed={panel === "mail"}
                onClick={() => togglePanel("mail")}
              >
                {t("newsletterMailShort", "Email account")}
              </button>
            </>
          ) : null}
        </div>
      ) : null}
      {poster && panel === "posts" ? <NewsletterPostComposer /> : null}
      {staff && panel === "cycle" ? (
        <NewsletterCycleSetup
          cycle={cycle}
          groups={groups}
          busy={busy}
          recipients={recipients}
          onSave={onSave}
          onPreview={onPreview}
          onClose={onClose}
        />
      ) : null}
      {staff && panel === "mail" ? <NewsletterMailSettings onChanged={loadRecipients} /> : null}

      {issue && !preview && (staff || issue.public_url) ? (
        <div className="pingflo-issue-meta">
          {staff ? (
            <span>
              {t("newsletterDelivered", {
                count: issue.emails_sent || 0,
                defaultValue: "Emailed to {{count}} members",
              })}
              {issue.emails_failed ? (
                <span className="pingflo-error-inline">
                  {" · "}
                  {t("newsletterFailedCount", {
                    count: issue.emails_failed,
                    defaultValue: "{{count}} failed",
                  })}
                </span>
              ) : null}
            </span>
          ) : null}
          {staff && issue.emails_failed ? (
            <button type="button" disabled={busy} onClick={onResend}>
              {t("newsletterResendFailed", "Resend failed emails")}
            </button>
          ) : null}
          {issue.public_url ? (
            <button type="button" onClick={onShare}>
              {t("newsletterShare", "Share this issue")}
            </button>
          ) : null}
        </div>
      ) : null}

      {snapshot ? (
        <NewsletterIssueBody snapshot={snapshot} />
      ) : (
        <p className="pingflo-empty">
          {t("newsletterEmpty", "The first Pingflo issue appears when this cycle closes.")}
        </p>
      )}

      {issues.length > 1 ? (
        <nav className="pingflo-archive" aria-label={t("newsletterArchive", "Earlier issues")}>
          <h2>{t("newsletterArchive", "Earlier issues")}</h2>
          <ul>
            {issues.map((row) => (
              <li key={row.issue_id}>
                <Link
                  to={`/newsletter/${row.issue_id}`}
                  aria-current={row.issue_id === issueId ? "page" : undefined}
                >
                  {formatIssueRange(row.start_date, row.end_date, i18n.language)}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      ) : null}
    </main>
  );
}
